import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import MetadataGenerationModal from './MetadataGenerationModal';
import './PlotsListEditor.css';

interface PlotsListEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PlotsListEditor: React.FC<PlotsListEditorProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId } = useParams<{ novelId: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plots, setPlots] = useState<string[]>([]);
  
  // Generation modal state
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState<any[] | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getNovel(novelId);
      setNovel(data);
      setPlots(data.metadata?.plots || []);
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/novel/${novelId}/metadata`);
  };

  const handleAddPlot = () => {
    const newPlots = [...plots, ''];
    setPlots(newPlots);
    savePlots(newPlots);
    // Navigate to the new plot editor
    navigate(`/novel/${novelId}/metadata/plots/${newPlots.length - 1}`);
  };

  const handleEditPlot = (index: number) => {
    navigate(`/novel/${novelId}/metadata/plots/${index}`);
  };

  const handleMovePlot = (index: number, direction: 'up' | 'down') => {
    const newPlots = [...plots];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newPlots.length) return;
    
    [newPlots[index], newPlots[targetIndex]] = [newPlots[targetIndex], newPlots[index]];
    setPlots(newPlots);
    savePlots(newPlots);
  };

  const handleDeletePlot = async (index: number) => {
    if (!window.confirm('Are you sure you want to delete this plot?')) return;
    
    const newPlots = plots.filter((_, i) => i !== index);
    setPlots(newPlots);
    await savePlots(newPlots);
  };

  const savePlots = async (updatedPlots: string[]) => {
    if (!novelId || !novel) return;

    try {
      const metadata = { ...novel.metadata, plots: updatedPlots };
      await NovelService.updateNovelMetadata(novelId, novel.genre || '', metadata);
    } catch (err) {
      console.error('Error saving plots:', err);
      setError('Failed to save plots');
    }
  };

  const handleOpenGenerationModal = () => {
    setGeneratedData(null);
    setGenerationError(null);
    setShowGenerationModal(true);
  };

  const handleGenerateFromChapters = async (chapterIds: string[]) => {
    if (!novelId) return;

    try {
      setIsGenerating(true);
      setGenerationError(null);
      
      const result = await NovelService.generateMetadataFromChapters(
        novelId,
        chapterIds,
        'plots'
      );

      setGeneratedData(result.plots || []);
    } catch (err: any) {
      console.error('Error generating metadata:', err);
      setGenerationError(err.message || 'Failed to generate metadata');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptGenerated = async (data: any[]) => {
    setPlots(data);
    await savePlots(data);
    setShowGenerationModal(false);
    setGeneratedData(null);
  };

  const getPlotPreview = (plot: string) => {
    if (!plot || plot.trim() === '') return '(Empty plot)';
    return plot.length > 150 ? plot.substring(0, 150) + '...' : plot;
  };

  if (loading) {
    return (
      <div className="plots-list-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="plots-list-editor-loading">Loading plots...</div>
      </div>
    );
  }

  if (error || !novel) {
    return (
      <div className="plots-list-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="plots-list-editor-error">
          {error || 'Novel not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="plots-list-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">{novel.title} /plots</h1>
        }
        leftActions={
          <button 
            className="plots-list-back-btn"
            onClick={handleBack}
          >
            ← Back
          </button>
        }
        rightActions={
          <div className="plots-list-header-actions">
            <button 
              className="plots-list-generate-btn"
              onClick={handleOpenGenerationModal}
              disabled={!novel?.chapters || novel.chapters.length === 0}
              title="Generate plots from chapters"
            >
              ✨ Generate
            </button>
            <button 
              className="plots-list-add-btn"
              onClick={handleAddPlot}
            >
              + New Plot
            </button>
          </div>
        }
      />

      <div className="plots-list-editor-content">
        {plots.length === 0 ? (
          <div className="plots-list-empty">
            <p>No plots yet</p>
            <button className="plots-list-add-btn-large" onClick={handleAddPlot}>
              + Add First Plot
            </button>
          </div>
        ) : (
          <div className="plots-list">
            {plots.map((plot, index) => (
              <div key={index} className="plot-list-item">
                <div className="plot-list-item-header">
                  <span className="plot-list-item-number">Plot {index + 1}</span>
                  <div className="plot-list-item-actions">
                    <button
                      className="plot-list-move-btn"
                      onClick={() => handleMovePlot(index, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="plot-list-move-btn"
                      onClick={() => handleMovePlot(index, 'down')}
                      disabled={index === plots.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      className="plot-list-delete-btn"
                      onClick={() => handleDeletePlot(index)}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p className="plot-list-item-preview">{getPlotPreview(plot)}</p>
                <button
                  className="plot-list-edit-btn"
                  onClick={() => handleEditPlot(index)}
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Generation Modal */}
      <MetadataGenerationModal
        isOpen={showGenerationModal}
        onClose={() => setShowGenerationModal(false)}
        fieldType="plots"
        chapters={novel?.chapters || []}
        currentData={plots}
        onGenerate={handleGenerateFromChapters}
        onAccept={handleAcceptGenerated}
        generatedData={generatedData}
        isGenerating={isGenerating}
        error={generationError}
      />
    </div>
  );
};

export default PlotsListEditor;

